import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import ReactCollapse from 'react-collapse';
import { debounce } from 'lodash';

import { LocaleService, WindowProxy, UrlService } from '@sm360/widget-commons-core-services';
import {
    AccountService,
    LocalStorageService,
    ModalService,
    REDIRECTIONS,
    MEDIA_QUERIES,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
    PriceAmounts,
} from '@sm360/widget-commons-general';
import { InputFormatCurrency } from '@sm360/widget-commons-forms';
import { CurrencyFormats, EVENTS, ShowroomService, Vehicle, ModelYear } from '@sm360/widget-commons-vehicle';

import 'payment-calculator/components/PaymentCalculatorDetails/components/CalculatorTradein/CalculatorTradein.scss';

class CalculatorTradein extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            inputs: {
                tradeIn: props.tradeIn,
                lien: props.lien,
            },
            hasTradeIn: true,
            showTradein: false,
        };

        this.setTradeIn = this.setTradeIn.bind(this);
        this.setLien = this.setLien.bind(this);
        this.ignoreInput = this.ignoreInput.bind(this);
        this.onAuthentication = this.onAuthentication.bind(this);
        this.onTradeInClick = this.onTradeInClick.bind(this);
        this.redirectToOfficialTradeIn = this.redirectToOfficialTradeIn.bind(this);
        this.showTradein = this.showTradein.bind(this);
        this.handleTradeInChange = this.handleTradeInChange.bind(this);
        this.tradeInAnalytics = this.tradeInAnalytics.bind(this);
        this.debounceTradeInAnalytics = debounce(this.tradeInAnalytics, 5000);
        this.isOfficialAmountExpired = this.isOfficialAmountExpired.bind(this);
        this.getOfficialRedirectionURL = this.getOfficialRedirectionURL.bind(this);
        this.getOfficialEvaluationAmount = this.getOfficialEvaluationAmount.bind(this);
        this.isOfficialRedirectionAllowed = this.isOfficialRedirectionAllowed.bind(this);
    }

    componentDidMount() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.addEventListener(EVENTS.AUTHENTICATED, this.onAuthentication);
        }
        if (!this.state.tradeIn && !!LocalStorageService.get('tradeIn')) {
            this.setTradeIn(LocalStorageService.get('tradeIn'), true);
        }
        const locale = LocaleService.getSimpleLocale();
        this.redirect = REDIRECTIONS[locale].tradeIn;
    }

    componentWillUnmount() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.removeEventListener(EVENTS.AUTHENTICATED, this.onAuthentication);
        }
    }

    onAuthentication() {
        return ShowroomService.loadTradeInVehicle().then((tradeIn) => {
            if (!tradeIn || !tradeIn.amount) {
                this.setTradeIn(LocalStorageService.get('tradeIn') || 0);
            } else {
                this.setTradeIn(tradeIn.amount);
            }
        });
    }

    onTradeInClick() {
        if (!AccountService.isAuthenticated()) {
            const LoginModal = ModalService.get('login/SignUpModal');
            LoginModal.addCallbacks({
                onClose: () => {
                    if (AccountService.isAuthenticated()) {
                        return this.redirectToOfficialTradeIn();
                    }
                    return true;
                },
            });
            LoginModal.open();
        } else {
            this.redirectToOfficialTradeIn();
        }
    }

    setTradeIn(value, isNotFromUser) {
        const inputs = { ...this.state.inputs };
        const parsedValue = !Number.isFinite(Number.parseInt(value, 10)) ? 0 : Number.parseInt(value, 10);
        inputs.tradeIn = parsedValue;
        this.props.setTradeIn(parsedValue, !!isNotFromUser);
        if (parsedValue <= 0) {
            inputs.lien = 0;
            this.props.setLien(0);
        }
        this.setState({ inputs });
    }

    setLien(value) {
        const parsedValue = !Number.isFinite(Number.parseInt(value, 10)) ? 0 : Number.parseInt(value, 10);
        this.props.setLien(parsedValue);
    }

    getOfficialEvaluationAmount() {
        const { tradeInVehicle } = this.props;
        return (
            tradeInVehicle?.tradeInExtraInfo?.evaluatorValues?.estimatedValue ||
            tradeInVehicle?.evaluatorValues?.estimatedValue ||
            0
        );
    }

    getOfficialRedirectionURL() {
        const locale = LocaleService.getSimpleLocale();
        if (!this.getOfficialEvaluationAmount() > 0) {
            return REDIRECTIONS[locale].newTradeInAppraisal;
        } else if (this.isOfficialAmountExpired()) {
            return REDIRECTIONS[locale].editExistingTradeInOfficial;
        }
        // if not demo integration site
        return REDIRECTIONS[locale].newTradeInOfficial;
    }

    tradeInAnalytics(value) {
        if (value)
            AnalyticsService.ga4Trigger(
                TAGS.PAYMENT_CALCULATOR_TRADEINVALUE(value, this.props.modelYear.model, this.props.modelYear.year)
            );
    }

    handleTradeInChange(event) {
        this.debounceTradeInAnalytics(event.target.value);
    }

    redirectToOfficialTradeIn() {
        const windowProxy = WindowProxy.get();

        if (windowProxy) {
            let url = this.getOfficialRedirectionURL();

            const quoteId = UrlService.getUrlParameter('quoteId');
            const writeToken = UrlService.getUrlParameter('writeToken');
            const params = [];

            if (quoteId) {
                params.push(`quoteId=${quoteId}`);
            }

            if (writeToken) {
                params.push(`writeToken=${writeToken}`);
            }

            if (params.length) {
                url += `&${params.join('&')}`;
            }

            windowProxy.location.href = url;
        }
    }

    ignoreInput() {
        if (this.state.hasTradeIn) {
            this.setTradeIn(0);
        } else {
            this.setTradeIn(this.state.inputs.tradeIn);
            if (this.state.inputs.tradeIn > 0) {
                this.setLien(this.state.inputs.lien);
            }
        }
        this.setState((prevState) => {
            return { hasTradeIn: !prevState.hasTradeIn };
        });
    }

    showTradein() {
        this.setState((prevState) => {
            return { showTradein: !prevState.showTradein };
        });
    }

    isOfficialAmountExpired() {
        // official evaluation value expiry date
        const { tradeInVehicle } = this.props;
        const officialAmountExp =
            Date.parse(
                tradeInVehicle?.tradeInExtraInfo?.evaluatorValues?.expirationDate ||
                    tradeInVehicle?.evaluatorValues?.expirationDate
            ) || 0;
        return !!officialAmountExp && new Date(officialAmountExp) <= new Date();
    }

    // To check if official redirection is allowed or not
    isOfficialRedirectionAllowed() {
        return !(this.getOfficialEvaluationAmount() > 0 && !this.isOfficialAmountExpired());
    }

    renderCreditTaxes() {
        const tradeInAmount = this.props.tradeInVehicle.amount;
        const taxCreditsAmount = this.props.tradeInVehicle.getTaxCredits();
        const tradeInAmountWithTaxesCredit = Number.parseInt(tradeInAmount, 10) + Number.parseInt(taxCreditsAmount, 10);
        const taxCredit = LocaleService.t('PAYMENT_CALCULATOR.TRADEIN.TAX_CREDIT');
        return (
            <div className="calculator-taxes-credit is-light">
                + <PriceAmounts price={LocaleService.l(taxCreditsAmount, CurrencyFormats.CURRENCY_2)} /> ({taxCredit}) ={' '}
                <PriceAmounts price={LocaleService.l(tradeInAmountWithTaxesCredit, CurrencyFormats.CURRENCY_2)} />
            </div>
        );
    }

    render() {
        const tradeInVehicleIsLocked = this.props.tradeInVehicle && this.props.tradeInVehicle.checkIsLocked();
        const inputSectionClasses = ['clearfix'];
        let isLienAvailableClass = 'calculator-lien-value is-light ';
        let isDisabled = false;
        if (!this.state.hasTradeIn || tradeInVehicleIsLocked) {
            inputSectionClasses.push('forms_disabled');
        }

        if (!this.state.hasTradeIn || tradeInVehicleIsLocked || this.state.inputs.tradeIn <= 0) {
            isLienAvailableClass += 'forms_disabled';
            isDisabled = true;
        }

        /* TODO: Put back when checkbox will be back
        const iconCheckbox = !this.state.hasTradeIn ? (
            'icon-Check-Box-On'
        ) : (
            'icon-Check-Box-Off'
        ); */
        const hasTradeInValue = !!Number.parseInt(this.props.tradeInVehicle.amount, 10);

        const renderBlock = (
            <div>
                <div className={inputSectionClasses}>
                    <div className="calculator-trade-value is-light" onChange={this.handleTradeInChange}>
                        <InputFormatCurrency
                            canBeNegative={false}
                            shouldDisplayError={false}
                            financeValue={this.props.tradeIn}
                            idSuffix="-tradeIn"
                            onDispatch={this.setTradeIn}
                            onFocus={this.props.setShiftEvent}
                            label={LocaleService.t('PAYMENT_CALCULATOR.TRADEIN.ESTIMATED_EXCHANGE')}
                        />
                    </div>
                    <div className={isLienAvailableClass}>
                        <InputFormatCurrency
                            canBeNegative={false}
                            shouldDisplayError={false}
                            financeValue={this.props.lien}
                            idSuffix="-lien"
                            onDispatch={this.setLien}
                            label={LocaleService.t('PAYMENT_CALCULATOR.TRADEIN.STILL_TO_BE_PAID')}
                            isDisabled={isDisabled}
                            onFocus={this.props.setShiftEvent}
                        />
                    </div>
                </div>

                {/* TODO: PUT IT BACK -- Enlever temporairement pour un bug.
                <div className="calculator-tradeIn-ignore is-light" onClick={this.ignoreInput}>
                    <icon className={iconCheckbox} /> {LocaleService.t('PAYMENT_CALCULATOR.TRADEIN.IGNORE_VALUE')}
                </div> */}
                {this.props.displayTradeInCreditRate && hasTradeInValue && this.renderCreditTaxes()}

                <button
                    className={`sr-link ${!this.isOfficialRedirectionAllowed() && 'disabled'}`}
                    onClick={this.onTradeInClick}
                >
                    {LocaleService.t('PAYMENT_CALCULATOR_V25.TRADEIN.GET_TRADE_APPRAISAL')}
                </button>
            </div>
        );

        const openSectionIcon = this.state.showTradein ? 'icon-chevron-thin-down reverse' : 'icon-chevron-thin-down';

        return (
            <div className="calculator-tradeIn">
                <div className="calculator-tradeIn-title is-light" onClick={this.showTradein}>
                    {LocaleService.t('PAYMENT_CALCULATOR.TRADEIN.TITLE')}
                    <icon className={openSectionIcon} />
                </div>
                <MediaQuery maxWidth={MEDIA_QUERIES.TABLET.MAX}>
                    <ReactCollapse isOpened={this.state.showTradein}>{renderBlock}</ReactCollapse>
                </MediaQuery>
                <MediaQuery minWidth={MEDIA_QUERIES.DESKTOP.MIN}>{renderBlock}</MediaQuery>
            </div>
        );
    }
}

CalculatorTradein.defaultProps = {
    tradeInVehicle: new Vehicle.TradeInVehicleModel(),
    displayTradeInCreditRate: true,
};

CalculatorTradein.propTypes = {
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel),
    tradeIn: PropTypes.number.isRequired,
    lien: PropTypes.number.isRequired,
    setTradeIn: PropTypes.func.isRequired,
    setLien: PropTypes.func.isRequired,
    modelYear: PropTypes.instanceOf(ModelYear.ModelYearModel).isRequired,
    displayTradeInCreditRate: PropTypes.bool,
};

CalculatorTradein.displayName = 'CalculatorTradein';

export default CalculatorTradein;
